import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeftRight, Baby, Flag, MessageSquare } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { api, call, type Schemas } from '../../api/client';
import { useAction, useComments, useMembers, usePriorities, useTask, useTaskHistory } from '../../api/queries';
import { useHousehold } from '../../api/session';
import { Avatar, Badge, Button, Card, ErrorText, Field, Input, Modal, PageHeader, Select, Spinner, Textarea } from '../../components/ui';
import { dueLabel, priorityTone, timeAgo } from '../../lib/format';
import { useTaskCommands } from './task-actions';
import { ParentLink, Subtasks } from './Subtasks';
import { TaskFormDialog } from './TaskFormDialog';

type Task = Schemas['TaskResponse'];

export function TaskDetailPage() {
  const { taskId } = useParams({ from: '/app/tasks/$taskId' });
  const q = useTask(taskId);
  if (q.isLoading) return <Spinner />;
  if (!q.data) return <ErrorText error={q.error} />;
  return <TaskDetail task={q.data} />;
}

function TaskDetail({ task }: { task: Task }) {
  const { memberId } = useHousehold();
  const members = useMembers().data ?? [];
  const name = (id: string | null | undefined) => (id === memberId ? 'You' : (members.find((m) => m.id === id)?.displayName ?? 'Someone'));
  const { simple, complete } = useTaskCommands();
  const [dialog, setDialog] = useState<null | 'edit' | 'transfer' | 'delegate' | 'priority' | 'points'>(null);
  const can = (a: Task['allowedActions'][number]) => task.allowedActions.includes(a);
  const busy = simple.isPending || complete.isPending;
  const act = (action: Parameters<typeof simple.mutate>[0]['action']) => simple.mutate({ action, taskId: task.id });

  return (
    <>
      <PageHeader
        title={task.title}
        subtitle={
          <span className="flex flex-wrap items-center gap-1.5">
            <Badge tone={priorityTone(task.priority.code)}><Flag size={11} /> {task.priority.label}</Badge>
            {dueLabel(task) ? <Badge tone={task.overdue ? 'danger' : 'neutral'}>{dueLabel(task)}</Badge> : null}
            <Badge>{task.status === 'OPEN' ? (task.assignee ? `With ${name(task.assignee.memberId)}` : 'House Pool') : task.status.toLowerCase()}</Badge>
            {task.points ? <Badge tone="accent">⭐ {pointsLabel(task)}</Badge> : null}
            {task.seriesId ? <Link to="/recurring/$seriesId" params={{ seriesId: task.seriesId }} className="text-xs underline">Part of a recurring task</Link> : null}
            <ParentLink parent={task.parent} />
          </span>
        }
      />

      <div className="mb-5 flex flex-wrap gap-2">
        {can('CLAIM') ? <Button busy={busy} onClick={() => act('claim')}>I'll do this</Button> : null}
        {can('COMPLETE') ? <Button busy={busy} onClick={() => complete.mutate({ taskId: task.id })}>{task.assignee?.role === 'CHILD' ? `Mark done for ${task.assignee.displayName}` : 'Mark done'}</Button> : null}
        {can('CLAIM_AND_COMPLETE') ? <Button variant="secondary" busy={busy} onClick={() => complete.mutate({ taskId: task.id, claimIfUnassigned: true })}>Already did it</Button> : null}
        {can('TAKE_OVER') ? <Button variant="secondary" busy={busy} onClick={() => act('take-over')}>I'll handle this one</Button> : null}
        {can('HAND_BACK_TO_CHILD') ? <Button variant="secondary" busy={busy} onClick={() => act('hand-back-to-child')}>Give back to child</Button> : null}
        {can('REQUEST_TRANSFER') ? <Button variant="secondary" onClick={() => setDialog('transfer')}><ArrowLeftRight size={15} /> Ask partner to take it</Button> : null}
        {can('PROPOSE_DELEGATION') ? <Button variant="secondary" onClick={() => setDialog('delegate')}><Baby size={15} /> Give to a child…</Button> : null}
        {can('PROPOSE_PRIORITY') && !task.pending.priorityRequest ? <Button variant="secondary" onClick={() => setDialog('priority')}><Flag size={15} /> Suggest a priority</Button> : null}
        {can('EDIT') ? <Button variant="ghost" onClick={() => setDialog('edit')}>Edit</Button> : null}
        {can('SET_POINTS') && !can('EDIT') ? <Button variant="ghost" onClick={() => setDialog('points')}>Change points</Button> : null}
        {can('RETURN_TO_POOL') ? <Button variant="ghost" busy={busy} onClick={() => act('return-to-pool')}>Put back in the House Pool</Button> : null}
        {can('WITHDRAW_FROM_CHILD') ? <Button variant="ghost" busy={busy} onClick={() => act('withdraw-from-child')}>Take back from child</Button> : null}
        {can('SKIP') ? <Button variant="ghost" busy={busy} onClick={() => act('skip')}>Skip this time</Button> : null}
        {can('REOPEN') ? <Button variant="secondary" busy={busy} onClick={() => act('reopen')}>Reopen</Button> : null}
        {can('ARCHIVE') ? <Button variant="ghost" busy={busy} onClick={() => act('archive')}>Remove</Button> : null}
      </div>

      <PendingPanels task={task} name={name} />

      {task.description ? <Card className="mb-4 whitespace-pre-wrap text-sm">{task.description}</Card> : null}
      <Subtasks task={task} />
      <p className="mb-6 text-xs text-[var(--muted)]">
        Added by {name(task.createdBy.memberId)} · {timeAgo(task.createdAt)}
        {task.estimatedMinutes ? ` · about ${task.estimatedMinutes} min` : ''}
      </p>

      <div className="grid gap-6 md:grid-cols-2">
        <Comments taskId={task.id} canComment={can('COMMENT')} />
        <History taskId={task.id} />
      </div>

      {dialog === 'edit' ? <TaskFormDialog open onOpenChange={() => setDialog(null)} task={task} /> : null}
      {dialog === 'transfer' ? <TransferDialog task={task} onClose={() => setDialog(null)} /> : null}
      {dialog === 'delegate' ? <DelegateDialog task={task} onClose={() => setDialog(null)} /> : null}
      {dialog === 'priority' ? <PriorityDialog task={task} onClose={() => setDialog(null)} /> : null}
      {dialog === 'points' ? <PointsDialog task={task} onClose={() => setDialog(null)} /> : null}
    </>
  );
}

function PendingPanels({ task, name }: { task: Task; name: (id: string | null | undefined) => string }) {
  const { familyId, memberId } = useHousehold();
  const t = task.pending.transfer;
  const respond = useAction(({ id, action, reason }: { id: string; action: 'accept' | 'decline' | 'cancel'; reason?: string }) => {
    const params = { params: { path: { familyId, requestId: id } } };
    if (action === 'accept') return call(api.POST('/api/v1/families/{familyId}/transfer-requests/{requestId}/accept', params));
    if (action === 'cancel') return call(api.POST('/api/v1/families/{familyId}/transfer-requests/{requestId}/cancel', params));
    return call(api.POST('/api/v1/families/{familyId}/transfer-requests/{requestId}/decline', { ...params, body: { reason } }));
  });
  const [declining, setDeclining] = useState(false);
  return (
    <div className="mb-4 space-y-3">
      {t ? (
        <Card className="border-sky-300 bg-sky-50 text-sm dark:bg-sky-900/20">
          <p>
            <strong>{name(t.fromMemberId)}</strong> asked <strong>{name(t.toMemberId)}</strong> to take this.
          </p>
          {t.toMemberId === memberId ? (
            declining ? (
              <form
                className="mt-3 flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  respond.mutate({ id: t.id, action: 'decline', reason: String(new FormData(e.currentTarget).get('reason')) || undefined });
                }}
              >
                <Input name="reason" placeholder="Why not? (optional)" maxLength={500} />
                <Button type="submit" variant="secondary" busy={respond.isPending}>Decline</Button>
              </form>
            ) : (
              <div className="mt-3 flex gap-2">
                <Button size="sm" busy={respond.isPending} onClick={() => respond.mutate({ id: t.id, action: 'accept' })}>Accept</Button>
                <Button size="sm" variant="secondary" onClick={() => setDeclining(true)}>Decline…</Button>
              </div>
            )
          ) : t.fromMemberId === memberId ? (
            <Button size="sm" variant="ghost" className="mt-2" busy={respond.isPending} onClick={() => respond.mutate({ id: t.id, action: 'cancel' })}>Cancel request</Button>
          ) : null}
        </Card>
      ) : null}
      {task.pending.delegation ? (
        <Card className="border-sky-300 bg-sky-50 text-sm dark:bg-sky-900/20">
          Proposed for <strong>{name(task.pending.delegation.childMemberId)}</strong> —{' '}
          {task.pending.delegation.status === 'DISPUTED' ? 'under discussion' : 'waiting for approval'}.{' '}
          <Link to="/decisions/delegations/$proposalId" params={{ proposalId: task.pending.delegation.id }} className="underline">Open the proposal</Link>
        </Card>
      ) : null}
      {task.pending.priorityRequest ? (
        <Card className="border-amber-300 bg-amber-50 text-sm dark:bg-amber-900/20">
          Priority under discussion: {task.priority.label} → <strong>{task.pending.priorityRequest.proposedPriority.toLowerCase()}</strong> proposed.{' '}
          {task.pending.priorityRequest.awaitingMemberId === memberId ? 'Your turn to respond. ' : ''}
          <Link to="/decisions/priority/$requestId" params={{ requestId: task.pending.priorityRequest.id }} className="underline">Open the discussion</Link>
        </Card>
      ) : null}
    </div>
  );
}

function TransferDialog({ task, onClose }: { task: Task; onClose(): void }) {
  const { familyId, memberId } = useHousehold();
  const adults = (useMembers().data ?? []).filter((m) => m.role === 'ADULT' && m.id !== memberId);
  const send = useAction(
    (f: FormData) =>
      call(
        api.POST('/api/v1/families/{familyId}/tasks/{taskId}/transfer-requests', {
          params: { path: { familyId, taskId: task.id } },
          body: { toMemberId: String(f.get('to')), message: (f.get('message') as string) || undefined },
        }),
      ),
    { success: 'Request sent' },
  );
  return (
    <Modal open onOpenChange={onClose} title="Ask your partner to take it">
      <form className="space-y-4" onSubmit={(e: FormEvent<HTMLFormElement>) => { e.preventDefault(); send.mutate(new FormData(e.currentTarget), { onSuccess: onClose }); }}>
        <p className="text-sm text-[var(--muted)]">It stays yours until they accept. They can say no.</p>
        <Field label="Ask">{(id) => <Select id={id} name="to">{adults.map((a) => <option key={a.id} value={a.id}>{a.displayName}</option>)}</Select>}</Field>
        <Field label="Message (optional)">{(id) => <Textarea id={id} name="message" maxLength={500} placeholder="You're already going to the hardware store tomorrow." />}</Field>
        <ErrorText error={send.error} />
        <div className="flex justify-end gap-2"><Button type="button" variant="ghost" onClick={onClose}>Cancel</Button><Button type="submit" busy={send.isPending} disabled={!adults.length}>Send request</Button></div>
      </form>
    </Modal>
  );
}

function DelegateDialog({ task, onClose }: { task: Task; onClose(): void }) {
  const { familyId } = useHousehold();
  const children = (useMembers().data ?? []).filter((m) => m.role === 'CHILD');
  const send = useAction(
    (f: FormData) =>
      call(
        api.POST('/api/v1/families/{familyId}/tasks/{taskId}/child-delegations', {
          params: { path: { familyId, taskId: task.id } },
          body: {
            childMemberId: String(f.get('child')),
            title: String(f.get('title')),
            description: (f.get('description') as string) || null,
            ...(task.parent ? {} : { points: f.get('points') ? Number(f.get('points')) : null }),
            note: (f.get('note') as string) || undefined,
          },
        }),
      ),
    { success: 'Proposal sent' },
  );
  return (
    <Modal open onOpenChange={onClose} title="Give this to a child">
      {!children.length ? (
        <p className="text-sm">Add a child in <Link to="/children" className="underline">Children</Link> first.</p>
      ) : (
        <form className="space-y-4" onSubmit={(e: FormEvent<HTMLFormElement>) => { e.preventDefault(); send.mutate(new FormData(e.currentTarget), { onSuccess: onClose }); }}>
          <p className="text-sm text-[var(--muted)]">Both parents agree before a chore goes to a child. Your partner will be asked; the child sees nothing until it's approved.</p>
          <Field label="Child">{(id) => <Select id={id} name="child">{children.map((c) => <option key={c.id} value={c.id}>{c.displayName}</option>)}</Select>}</Field>
          <Field label="What the child is asked to do">{(id) => <Input id={id} name="title" required maxLength={200} defaultValue={task.title} />}</Field>
          <Field label="Instructions for the child (optional)">{(id) => <Textarea id={id} name="description" maxLength={4000} defaultValue={task.description ?? ''} />}</Field>
          {task.parent ? (
            <p className="text-sm">Points: <strong>{task.points ?? 0}</strong>, this subtask's share of “{task.parent.title}”.</p>
          ) : (
            <Field label="Points">{(id) => <Input id={id} name="points" type="number" min={0} max={1000} defaultValue={task.points ?? ''} />}</Field>
          )}
          <Field label="Note to your partner (optional)">{(id) => <Textarea id={id} name="note" maxLength={1000} />}</Field>
          <ErrorText error={send.error} />
          <div className="flex justify-end gap-2"><Button type="button" variant="ghost" onClick={onClose}>Cancel</Button><Button type="submit" busy={send.isPending}>Propose</Button></div>
        </form>
      )}
    </Modal>
  );
}

function PriorityDialog({ task, onClose }: { task: Task; onClose(): void }) {
  const { familyId } = useHousehold();
  const priorities = (usePriorities().data ?? []).filter((p) => p.code !== task.priority.code);
  const send = useAction(
    (f: FormData) =>
      call(
        api.POST('/api/v1/families/{familyId}/tasks/{taskId}/priority-change-requests', {
          params: { path: { familyId, taskId: task.id } },
          body: { proposedPriority: String(f.get('priority')), reason: String(f.get('reason')) },
        }),
      ),
    { success: 'Suggestion sent' },
  );
  return (
    <Modal open onOpenChange={onClose} title="Suggest a different priority">
      <form className="space-y-4" onSubmit={(e: FormEvent<HTMLFormElement>) => { e.preventDefault(); send.mutate(new FormData(e.currentTarget), { onSuccess: onClose }); }}>
        <p className="text-sm text-[var(--muted)]">It's {task.assignee?.displayName}'s task, so they decide. The current priority ({task.priority.label}) stays until they agree.</p>
        <Field label="Suggested priority">{(id) => <Select id={id} name="priority">{priorities.map((p) => <option key={p.code} value={p.code}>{p.label}</option>)}</Select>}</Field>
        <Field label="Why?">{(id) => <Textarea id={id} name="reason" required maxLength={1000} placeholder="The leak is getting worse and there's water under the cabinet." />}</Field>
        <ErrorText error={send.error} />
        <div className="flex justify-end gap-2"><Button type="button" variant="ghost" onClick={onClose}>Cancel</Button><Button type="submit" busy={send.isPending}>Send</Button></div>
      </form>
    </Modal>
  );
}

/** Points on a done task can still be changed; a child's balance follows (the award is reversed and re-given). */
function PointsDialog({ task, onClose }: { task: Task; onClose(): void }) {
  const { familyId } = useHousehold();
  const save = useAction(
    (f: FormData) =>
      call(
        api.PATCH('/api/v1/families/{familyId}/tasks/{taskId}', {
          params: { path: { familyId, taskId: task.id } },
          body: { expectedVersion: task.version, points: f.get('points') ? Number(f.get('points')) : null },
        }),
      ),
    { success: 'Points changed' },
  );
  const child = task.assignee?.role === 'CHILD' ? task.assignee.displayName : null;
  return (
    <Modal open onOpenChange={onClose} title="Change points">
      <form className="space-y-4" onSubmit={(e: FormEvent<HTMLFormElement>) => { e.preventDefault(); save.mutate(new FormData(e.currentTarget), { onSuccess: onClose }); }}>
        {child ? <p className="text-sm text-[var(--muted)]">{child}'s points are corrected to match.</p> : null}
        {task.subtasks.total > 0 ? <p className="text-sm text-[var(--muted)]">Subtasks already done keep the share they had.</p> : null}
        <Field label="Points">{(id) => <Input id={id} name="points" type="number" min={0} max={1000} defaultValue={task.points ?? ''} autoFocus />}</Field>
        <ErrorText error={save.error} />
        <div className="flex justify-end gap-2"><Button type="button" variant="ghost" onClick={onClose}>Cancel</Button><Button type="submit" busy={save.isPending}>Save</Button></div>
      </form>
    </Modal>
  );
}

function Comments({ taskId, canComment }: { taskId: string; canComment: boolean }) {
  const { familyId } = useHousehold();
  const comments = useComments(taskId).data ?? [];
  const add = useAction((body: string) =>
    call(api.POST('/api/v1/families/{familyId}/tasks/{taskId}/comments', { params: { path: { familyId, taskId }, header: { 'Idempotency-Key': crypto.randomUUID() } }, body: { body } })),
  );
  return (
    <section>
      <h2 className="mb-2 flex items-center gap-2 text-sm font-medium text-[var(--muted)]"><MessageSquare size={15} /> Comments</h2>
      <div className="space-y-2">
        {comments.map((c) => (
          <div key={c.id} className="flex gap-2 text-sm">
            <Avatar name={c.author.displayName} size={24} />
            <div className="rounded-xl bg-[var(--surface-2)] px-3 py-2">
              <p className="text-xs text-[var(--muted)]">{c.author.displayName} · {timeAgo(c.createdAt)}</p>
              <p className="whitespace-pre-wrap">{c.body}</p>
            </div>
          </div>
        ))}
      </div>
      {canComment ? (
        <form
          className="mt-3 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const form = e.currentTarget;
            const body = String(new FormData(form).get('body')).trim();
            // Clear only if the box still holds what was posted (the list refresh runs first; a next comment may be typed).
            if (body) add.mutate(body, { onSuccess: () => { if (String(new FormData(form).get('body')).trim() === body) form.reset(); } });
          }}
        >
          <Input name="body" placeholder="Add a comment" maxLength={4000} />
          <Button type="submit" variant="secondary" busy={add.isPending}>Post</Button>
        </form>
      ) : null}
    </section>
  );
}

function History({ taskId }: { taskId: string }) {
  const items = useTaskHistory(taskId).data?.items ?? [];
  return (
    <section>
      <h2 className="mb-2 text-sm font-medium text-[var(--muted)]">History</h2>
      <ol className="space-y-2 border-l border-[var(--border)] pl-4">
        {items.map((e) => (
          <li key={e.id} className="text-sm">
            <p>{e.text}</p>
            <p className="text-xs text-[var(--muted)]">{timeAgo(e.occurredAt)}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function useBackToList() {
  return useNavigate();
}

/** Points shared by subtasks (ADR-0022): a subtask shows its share, a main task the total its subtasks share. */
function pointsLabel(task: Schemas['TaskResponse']): string {
  if (task.parent) return `${task.points} points (share of the main task)`;
  if (task.subtasks.total > 0) return `${task.points} points, shared by its subtasks`;
  return `${task.points} points`;
}
